import { supabase } from "@/lib/supabase.js";

/**
 * Cache for storing the list of available gradient avatars
 * This prevents repeated API calls to list the same files
 */
let gradientAvatarsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetches the list of gradient avatar files from the default directory
 * @returns {Promise<Array>} Array of gradient avatar file objects
 */
const fetchGradientAvatars = async () => {
  try {
    // Check if we have a valid cache
    if (
      gradientAvatarsCache &&
      cacheTimestamp &&
      Date.now() - cacheTimestamp < CACHE_DURATION
    ) {
      return gradientAvatarsCache;
    }

    const { data, error } = await supabase.storage
      .from("avatars")
      .list("default/", {
        sortBy: { column: "name", order: "asc" },
      });

    if (error) {
      console.error("Error fetching gradient avatars:", error);
      return [];
    }

    // Filter for image files only
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
    const gradientFiles = data.filter((file) => {
      const extension = file.name.toLowerCase().split(".").pop();
      return imageExtensions.includes(extension);
    });

    // Update cache
    gradientAvatarsCache = gradientFiles;
    cacheTimestamp = Date.now();

    return gradientFiles;
  } catch (error) {
    console.error("Error in fetchGradientAvatars:", error);
    return [];
  }
};

/**
 * Gets a random gradient avatar URL from the default directory
 * @returns {Promise<string|null>} The public URL of a random gradient avatar or null if none available
 */
export const getRandomGradientAvatar = async () => {
  try {
    const gradientFiles = await fetchGradientAvatars();

    if (gradientFiles.length === 0) {
      console.warn("No gradient avatars found in default directory");
      return null;
    }

    // Select a random file
    const randomIndex = Math.floor(Math.random() * gradientFiles.length);
    const selectedFile = gradientFiles[randomIndex];

    // Generate the public URL
    const { data } = supabase.storage
      .from("avatars")
      .getPublicUrl(`default/${selectedFile.name}`);

    return data.publicUrl;
  } catch (error) {
    console.error("Error getting random gradient avatar:", error);
    return null;
  }
};

/**
 * Gets a random gradient avatar URL synchronously using cached data
 * Falls back to async fetch if cache is empty
 * @returns {string|null} The public URL of a random gradient avatar or null if none available
 */
export const getRandomGradientAvatarSync = () => {
  try {
    // If we have cached data, use it
    if (gradientAvatarsCache && gradientAvatarsCache.length > 0) {
      const randomIndex = Math.floor(
        Math.random() * gradientAvatarsCache.length
      );
      const selectedFile = gradientAvatarsCache[randomIndex];

      const { data } = supabase.storage
        .from("avatars")
        .getPublicUrl(`default/${selectedFile.name}`);

      return data.publicUrl;
    }

    // If no cache, trigger async fetch for next time but return null for now
    fetchGradientAvatars().catch(console.error);
    return null;
  } catch (error) {
    console.error("Error getting random gradient avatar sync:", error);
    return null;
  }
};

/**
 * Preloads the gradient avatars cache
 * Call this early in the app lifecycle to ensure avatars are available
 * @returns {Promise<void>}
 */
export const preloadGradientAvatars = async () => {
  try {
    await fetchGradientAvatars();
  } catch (error) {
    console.error("Error preloading gradient avatars:", error);
  }
};

/**
 * Gets multiple random gradient avatar URLs (useful for generating options)
 * @param {number} count - Number of random avatars to get
 * @returns {Promise<Array<string>>} Array of public URLs
 */
export const getMultipleRandomGradientAvatars = async (count = 3) => {
  try {
    const gradientFiles = await fetchGradientAvatars();

    if (gradientFiles.length === 0) {
      return [];
    }

    const selectedUrls = [];
    const usedIndices = new Set();

    // Get unique random avatars
    while (
      selectedUrls.length < count &&
      usedIndices.size < gradientFiles.length
    ) {
      const randomIndex = Math.floor(Math.random() * gradientFiles.length);

      if (!usedIndices.has(randomIndex)) {
        usedIndices.add(randomIndex);
        const selectedFile = gradientFiles[randomIndex];

        const { data } = supabase.storage
          .from("avatars")
          .getPublicUrl(`default/${selectedFile.name}`);

        selectedUrls.push(data.publicUrl);
      }
    }

    return selectedUrls;
  } catch (error) {
    console.error("Error getting multiple random gradient avatars:", error);
    return [];
  }
};
